import axios from 'axios';
import { aiService } from './aiService';
import { knowledgeBaseApiService } from './knowledgeBaseApi';
import { chatHistoryApiService } from './chatHistoryApi';
import { hanafiyaDataApiService } from './hanafiyaDataApi';

// Types pour les messages
export interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  metadata?: {
    apiSource?: string;
    arabicText?: string;
    translation?: string;
    reference?: string;
    videoUrl?: string;
  };
}

// Types d'API externes supprimés - plus d'utilisation d'APIs externes

// Service de chat islamique
class IslamicChatService {
  private userLocation: { lat: number; lng: number; city: string } | null = null;

  constructor() {
    this.detectUserLocation();
  }

  // Détection de localisation désactivée (plus d'API externe)
  private async detectUserLocation(): Promise<void> {
    // Localisation désactivée - plus d'utilisation d'API externe
    this.userLocation = null;
  }

  // Détecter les questions de présentation/personnelles
  private isPersonalQuestion(message: string): boolean {
    const lowerMessage = message.toLowerCase().trim();
    const personalPatterns = [
      // Questions directes sur l'identité
      /^(qui.*es.*tu|who.*are.*you|t.*es.*qui|tu.*es.*qui)/i,
      /^(bonjour|salut|hello|hi|salam|assalamu).*(qui|who|t.*es|tu.*es)/i,
      /(qui|who).*(es|are).*(tu|you)/i,
      // Questions sur le nom
      /^(ton|your).*nom|name/i,
      /comment.*tu.*t.*appelle|what.*is.*your.*name/i,
      // Questions sur la création
      /qui.*créé|who.*created|qui.*t.*a.*fait|who.*made.*you/i,
      // Salutations simples (sans autre question)
      /^(bonjour|salut|hello|hi|salam|assalamu)\s*[!?]?$/i,
      // Présentation
      /^(présente|presente|introduce).*(toi|yourself)/i,
    ];
    return personalPatterns.some(pattern => pattern.test(lowerMessage));
  }

  // Répondre aux questions personnelles
  private getPersonalResponse(message: string): string {
    const lowerMessage = message.toLowerCase();
    
    if (/bonjour|salut|hello|hi|salam|assalamu/i.test(lowerMessage)) {
      return `🕌 **Assalamu Alaikum wa Rahmatullahi wa Barakatuh !**\n\n` +
        `Je suis **HanafiyaIA**, votre assistant intelligent pour tout ce qui concerne l'Islam et la confrérie Tidiane.\n\n` +
        `Je peux vous aider avec :\n` +
        `• Questions sur l'Islam et la confrérie Tidiane\n` +
        `• Informations sur les exposés, événements et daaras\n` +
        `• Consultation de la base de connaissances (Q/R)\n\n` +
        `Comment puis-je vous aider aujourd'hui ? 😊`;
    }
    
    if (/qui.*es.*tu|who.*are.*you|t.*es.*qui/i.test(lowerMessage)) {
      return `🕌 **Je suis HanafiyaIA**\n\n` +
        `Votre assistant intelligent dédié à l'Islam et la confrérie Tidiane. Je peux vous aider avec :\n\n` +
        `📚 **Base de connaissances** - Questions et réponses sur l'Islam\n` +
        `📖 **Exposés et événements** - Informations sur les activités du dahira\n` +
        `📿 **Daaras** - Cours et enseignements disponibles\n\n` +
        `Posez-moi n'importe quelle question, je suis là pour vous aider ! 🌟`;
    }
    
    return `🕌 **Assalamu Alaikum !**\n\n` +
      `Je suis **HanafiyaIA**, votre assistant intelligent pour tout ce qui concerne l'Islam et la confrérie Tidiane.\n\n` +
      `Comment puis-je vous aider aujourd'hui ?`;
  }

  // Détecter l'intention dans le message (simplifié - plus d'APIs externes)
  private detectIntent(message: string): string {
    // Questions personnelles (en premier pour les capturer)
    if (this.isPersonalQuestion(message)) {
      return 'personal';
    }

    // Par défaut, chercher dans la base de connaissances
    return 'general';
  }

  // Fonction supprimée - plus d'API externe pour les horaires de prière

  // Fonction supprimée - plus d'API externe pour les versets du Coran

  // Fonction supprimée - plus d'API externe pour les hadiths

  // Fonction supprimée - plus d'API externe pour les douas

  // Fonction supprimée - plus d'API externe pour les 99 noms d'Allah

  // Fonction supprimée - plus d'API externe pour les versets aléatoires

  // Traiter un message et obtenir une réponse (SYSTÈME HYBRIDE avec contexte conversationnel)
  async processMessage(
    message: string,
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>
  ): Promise<string> {
    const startTime = Date.now();
    const intent = this.detectIntent(message);
    let source: 'api' | 'knowledge_base' | 'ai' | 'fallback' = 'fallback';
    let apiType: undefined; // Plus d'APIs externes
    let aiModel: 'gemini' | 'groq' | undefined;
    let answer = '';

    try {
      // ÉTAPE 1: Vérifier les questions personnelles (PRIORITÉ ABSOLUE - ne pas chercher ailleurs)
      if (intent === 'personal') {
        source = 'fallback';
        answer = this.getPersonalResponse(message);
        
        // Enregistrer l'interaction et retourner immédiatement (pas de recherche dans KB/API/AI)
        const responseTime = Date.now() - startTime;
        await chatHistoryApiService.saveInteraction({
          question: message,
          answer: answer,
          source: source,
          responseTime: responseTime,
        });
        return answer;
      }

      // ÉTAPE 2: Chercher dans la base de connaissances (FAQ) - PRIORITÉ
      // Ne pas chercher dans la base si c'est une requête simple qui a réussi
      if (!answer) {
        let knowledgeEntry: any = null;
        try {
          // D'abord chercher dans la base de connaissances classique
          knowledgeEntry = await knowledgeBaseApiService.search(message);
          if (knowledgeEntry) {
            source = 'knowledge_base';
            answer = knowledgeEntry.answer + 
              (knowledgeEntry.arabic ? `\n\n**Arabe:** ${knowledgeEntry.arabic}` : '');
          } else {
            // Si pas trouvé, chercher dans les questions publiques répondues
            const publicQuestion = await knowledgeBaseApiService.searchPublicQuestions(message);
            if (publicQuestion) {
              source = 'knowledge_base'; // Utiliser le même source pour la cohérence
              let publicAnswer = '';
              
              // Ajouter la réponse texte si disponible
              if (publicQuestion.answer) {
                publicAnswer = publicQuestion.answer;
              }
              
              // Ajouter la réponse vidéo si disponible
              if (publicQuestion.answer_video) {
                if (publicAnswer) {
                  publicAnswer += `\n\n📹 **Réponse vidéo:**\n[VIDEO:${publicQuestion.answer_video}]`;
                } else {
                  publicAnswer = `📹 **Réponse vidéo:**\n[VIDEO:${publicQuestion.answer_video}]`;
                }
              }
              
              if (publicAnswer) {
                answer = publicAnswer;
              }
            }
          }
        } catch (error) {
          console.error('Erreur lors de la recherche dans la base de connaissances:', error);
        }
      }

      // ÉTAPE 3.5: Chercher dans les données Hanafiya (exposants, événements, daaras, marabouts) - PRIORITÉ
      // Utiliser directement les APIs du backend sans passer par l'IA
      if (!answer) {
        const hanafiyaQuery = hanafiyaDataApiService.detectHanafiyaQuery(message);
        if (hanafiyaQuery.type) {
          try {
            console.log(`[ChatService] Recherche dans données Hanafiya (${hanafiyaQuery.type}):`, hanafiyaQuery.query);
            let hanafiyaData: any = null;
            let allData: any[] = [];
            
            switch (hanafiyaQuery.type) {
              case 'expose':
                // Si la question demande "les exposés récents" ou "des exposés", récupérer plusieurs
                if (/récents|récent|récentes|liste|tous|all|des exposés|les exposés|parle.*des exposés|parle.*exposés/i.test(message)) {
                  allData = await hanafiyaDataApiService.getExposes(10);
                  console.log(`[ChatService] Exposés récupérés: ${allData.length}`);
                  if (allData.length > 0) {
                    answer = `📚 **Exposés récents** (${allData.length} trouvé${allData.length > 1 ? 's' : ''})\n\n`;
                    allData.forEach((expose: any, index: number) => {
                      answer += `${index + 1}. **${expose.theme}**\n`;
                      answer += `   👤 Exposant : ${expose.prenom_exposant} ${expose.nom_exposant}\n`;
                      answer += `   📅 Date : ${new Date(expose.date_expose).toLocaleDateString('fr-FR')} à ${expose.heure_debut}\n`;
                      answer += `   📍 Lieu : ${expose.lieu}\n`;
                      if (expose.description) {
                        answer += `   📝 ${expose.description.substring(0, 100)}${expose.description.length > 100 ? '...' : ''}\n`;
                      }
                      answer += `\n`;
                    });
                    source = 'api';
                  } else {
                    // Aucun exposé trouvé, donner un message informatif
                    answer = `📚 **Exposés récents**\n\n`;
                    answer += `❌ Aucun exposé récent trouvé dans la base de données pour le moment.\n\n`;
                    answer += `💡 **Conseil :** Les exposés sont ajoutés régulièrement par les administrateurs. Revenez plus tard ou consultez les événements à venir !`;
                    source = 'api';
                  }
                } else {
                  // Recherche spécifique
                  hanafiyaData = await hanafiyaDataApiService.searchExpose(hanafiyaQuery.query);
                  if (hanafiyaData) {
                    answer = `📚 **Exposé : ${hanafiyaData.theme}**\n\n` +
                      `👤 **Exposant :** ${hanafiyaData.prenom_exposant} ${hanafiyaData.nom_exposant}\n` +
                      `📅 **Date :** ${new Date(hanafiyaData.date_expose).toLocaleDateString('fr-FR')}\n` +
                      `🕐 **Heure :** ${hanafiyaData.heure_debut}\n` +
                      `📍 **Lieu :** ${hanafiyaData.lieu}\n` +
                      (hanafiyaData.description ? `\n📝 **Description :**\n${hanafiyaData.description}` : '');
                    source = 'api';
                  } else {
                    // Aucun exposé trouvé pour la recherche
                    answer = `📚 **Recherche d'exposé**\n\n`;
                    answer += `❌ Aucun exposé trouvé correspondant à "${hanafiyaQuery.query}".\n\n`;
                    answer += `💡 **Conseil :** Essayez une autre recherche ou consultez les exposés récents disponibles !`;
                    source = 'api';
                  }
                }
                break;
              case 'event':
                // Si la question demande "les événements" ou "des événements", récupérer plusieurs
                if (/récents|récent|récentes|liste|tous|all|des événements|les événements|à venir|parle.*événements/i.test(message)) {
                  allData = await hanafiyaDataApiService.getEvents(10);
                  console.log(`[ChatService] Événements récupérés: ${allData.length}`);
                  if (allData.length > 0) {
                    answer = `📅 **Événements** (${allData.length} trouvé${allData.length > 1 ? 's' : ''})\n\n`;
                    allData.forEach((event: any, index: number) => {
                      answer += `${index + 1}. **${event.titre}**\n`;
                      answer += `   📅 Date : ${new Date(event.date_event).toLocaleDateString('fr-FR')} à ${event.heure_debut}\n`;
                      answer += `   📍 Lieu : ${event.lieu}\n`;
                      answer += `   📝 ${event.description.substring(0, 100)}${event.description.length > 100 ? '...' : ''}\n\n`;
                    });
                    source = 'api';
                  } else {
                    answer = `📅 **Événements**\n\n`;
                    answer += `❌ Aucun événement trouvé dans la base de données pour le moment.\n\n`;
                    answer += `💡 **Conseil :** Les événements sont ajoutés régulièrement par les administrateurs. Revenez plus tard !`;
                    source = 'api';
                  }
                } else {
                  // Recherche spécifique
                  hanafiyaData = await hanafiyaDataApiService.searchEvent(hanafiyaQuery.query);
                  if (hanafiyaData) {
                    answer = `📅 **Événement : ${hanafiyaData.titre}**\n\n` +
                      `📝 **Description :**\n${hanafiyaData.description}\n\n` +
                      `📅 **Date :** ${new Date(hanafiyaData.date_event).toLocaleDateString('fr-FR')}\n` +
                      `🕐 **Heure :** ${hanafiyaData.heure_debut}\n` +
                      `📍 **Lieu :** ${hanafiyaData.lieu}`;
                    source = 'api';
                  } else {
                    answer = `📅 **Recherche d'événement**\n\n`;
                    answer += `❌ Aucun événement trouvé correspondant à "${hanafiyaQuery.query}".\n\n`;
                    answer += `💡 **Conseil :** Essayez une autre recherche ou consultez les événements récents disponibles !`;
                    source = 'api';
                  }
                }
                break;
              case 'daara':
                // Si la question demande "les daaras" ou "des daaras", récupérer plusieurs
                if (/liste|tous|all|des daaras|les daaras|disponibles/i.test(message)) {
                  allData = await hanafiyaDataApiService.getDaaras(20);
                  if (allData.length > 0) {
                    answer = `📖 **Daaras disponibles** (${allData.length} trouvé${allData.length > 1 ? 's' : ''})\n\n`;
                    allData.forEach((daara: any, index: number) => {
                      answer += `${index + 1}. **${daara.titre}**\n`;
                      answer += `   📅 Jour : ${daara.jour}\n`;
                      answer += `   📝 ${daara.description.substring(0, 100)}${daara.description.length > 100 ? '...' : ''}\n\n`;
                    });
                    source = 'api';
                  }
                } else {
                  // Recherche spécifique
                  hanafiyaData = await hanafiyaDataApiService.searchDaara(hanafiyaQuery.query);
                  if (hanafiyaData) {
                    answer = `📖 **Daara : ${hanafiyaData.titre}**\n\n` +
                      `📝 **Description :**\n${hanafiyaData.description}\n\n` +
                      `📅 **Jour :** ${hanafiyaData.jour}`;
                    source = 'api';
                  }
                }
                break;
              case 'marabout':
                // Si la question demande "les marabouts" ou "des marabouts", récupérer plusieurs
                if (/liste|tous|all|des marabouts|les marabouts|disponibles/i.test(message)) {
                  allData = await hanafiyaDataApiService.getMarabouts(20);
                  if (allData.length > 0) {
                    answer = `👨‍🏫 **Marabouts** (${allData.length} trouvé${allData.length > 1 ? 's' : ''})\n\n`;
                    allData.forEach((marabout: any, index: number) => {
                      answer += `${index + 1}. **${marabout.nom_complet}**\n`;
                      if (marabout.nom_arabe) {
                        answer += `   Nom arabe : ${marabout.nom_arabe}\n`;
                      }
                      answer += `   📝 ${marabout.biographie.substring(0, 150)}${marabout.biographie.length > 150 ? '...' : ''}\n\n`;
                    });
                    source = 'api';
                  }
                } else {
                  // Recherche spécifique
                  hanafiyaData = await hanafiyaDataApiService.searchMarabout(hanafiyaQuery.query);
                  if (hanafiyaData) {
                    answer = `👨‍🏫 **Marabout : ${hanafiyaData.nom_complet}**\n\n` +
                      (hanafiyaData.nom_arabe ? `**Nom arabe :** ${hanafiyaData.nom_arabe}\n\n` : '') +
                      `📖 **Biographie :**\n${hanafiyaData.biographie}` +
                      (hanafiyaData.periode ? `\n\n📅 **Période :** ${hanafiyaData.periode}` : '');
                    source = 'api';
                  }
                }
                break;
            }
          } catch (error) {
            console.error('[ChatService] Erreur lors de la recherche Hanafiya:', error);
          }
        }
      }

      // ÉTAPE 4: Questions ouvertes - Utiliser les données disponibles (IA temporairement désactivée)
      if (!answer && aiService.shouldUseAI(message)) {
        console.log('[ChatService] Question ouverte détectée, utilisation des données disponibles (IA désactivée)');
        
        // Essayer de trouver des informations pertinentes dans les données Hanafiya
        try {
          const exposes = await hanafiyaDataApiService.getExposes(5);
          const events = await hanafiyaDataApiService.getEvents(5);
          const daaras = await hanafiyaDataApiService.getDaaras(5);
          const marabouts = await hanafiyaDataApiService.getMarabouts(5);
          
          // Construire une réponse basée sur les données disponibles
          const hasData = exposes.length > 0 || events.length > 0 || daaras.length > 0 || marabouts.length > 0;
          
          if (hasData) {
            answer = `💡 **Informations disponibles dans notre base de données :**\n\n`;
            
            if (exposes.length > 0) {
              answer += `📚 **Exposés récents** (${exposes.length}) :\n`;
              exposes.slice(0, 3).forEach((e: any) => {
                answer += `• **${e.theme}** - ${e.prenom_exposant} ${e.nom_exposant} (${new Date(e.date_expose).toLocaleDateString('fr-FR')})\n`;
              });
              answer += `\n`;
            }
            
            if (events.length > 0) {
              answer += `📅 **Événements** (${events.length}) :\n`;
              events.slice(0, 3).forEach((e: any) => {
                answer += `• **${e.titre}** - ${new Date(e.date_event).toLocaleDateString('fr-FR')} à ${e.lieu}\n`;
              });
              answer += `\n`;
            }
            
            if (daaras.length > 0) {
              answer += `📖 **Daaras** (${daaras.length}) :\n`;
              daaras.slice(0, 3).forEach((d: any) => {
                answer += `• **${d.titre}** - ${d.jour}\n`;
              });
              answer += `\n`;
            }
            
            if (marabouts.length > 0) {
              answer += `👨‍🏫 **Marabouts** (${marabouts.length}) :\n`;
              marabouts.slice(0, 3).forEach((m: any) => {
                answer += `• **${m.nom_complet}**${m.nom_arabe ? ` (${m.nom_arabe})` : ''}\n`;
              });
              answer += `\n`;
            }
            
            answer += `💬 **Pour plus d'informations, posez-moi des questions spécifiques sur :**\n`;
            answer += `• Les exposants et leurs thèmes (ex: "Parle-moi des exposés récents")\n`;
            answer += `• Les événements à venir (ex: "Quels sont les événements de cette semaine ?")\n`;
            answer += `• Les cours daaras disponibles (ex: "Quels sont les daaras du vendredi ?")\n`;
            answer += `• Les marabouts et leurs biographies (ex: "Qui est Cheikh X ?")\n\n`;
            answer += `Ou utilisez les boutons rapides pour des réponses instantanées !`;
            
            source = 'api';
          } else {
            // Si aucune donnée disponible, donner un message d'aide
            answer = this.getHelpMessageWithSites();
          }
        } catch (error) {
          console.error('[ChatService] Erreur lors du chargement des données:', error);
          answer = this.getHelpMessageWithSites();
        }
      }

      // ÉTAPE 5: Fallback - TOUJOURS avoir une réponse (sans IA pour l'instant)
      if (!answer) {
        // Si c'est une réponse contextuelle courte (comme "A bon ?")
        const isShortFollowUp = message.length < 20 && /^(a bon|ok|d'accord|merci|oui|non|ah|okay|vraiment)\??$/i.test(message.trim());
        
        if (isShortFollowUp && conversationHistory && conversationHistory.length > 0) {
          // Réponse contextuelle simple sans IA
          answer = `Oui, c'est exact. Y a-t-il autre chose que je puisse vous aider à comprendre ?`;
          source = 'fallback';
        } else {
          // Dernière tentative : chercher dans la base de connaissances
          try {
            const kbEntry = await knowledgeBaseApiService.search(message);
            if (kbEntry) {
              source = 'knowledge_base';
              answer = kbEntry.answer + (kbEntry.arabic ? `\n\n**Arabe:** ${kbEntry.arabic}` : '');
            } else {
              answer = this.getHelpMessageWithSites();
            }
          } catch (kbError) {
            answer = this.getHelpMessageWithSites();
          }
        }
      }

      // Enregistrer l'interaction
      const responseTime = Date.now() - startTime;
      await chatHistoryApiService.saveInteraction({
        question: message,
        answer: answer,
        source: source,
        apiType: apiType,
        aiModel: aiModel,
        userLocation: this.userLocation || undefined,
        responseTime: responseTime,
      });

      return answer;
    } catch (error) {
      console.error('Erreur lors du traitement du message:', error);
      answer = '❌ Désolé, une erreur est survenue. Veuillez réessayer plus tard.';
      
      // Enregistrer l'erreur aussi
      await chatHistoryApiService.saveInteraction({
        question: message,
        answer: answer,
        source: 'fallback',
        responseTime: Date.now() - startTime,
      });
      
      return answer;
    }
  }

  // Fonction supprimée - plus d'utilisation d'APIs externes

  // Message d'aide
  private getHelpMessage(): string {
    return `🤔 **Je n'ai pas compris votre question**

Je peux vous aider avec :
• Questions sur l'Islam et la confrérie Tidiane
• Informations sur les exposés, événements et daaras
• Consultation de la base de connaissances (Q/R)

💡 **Conseil :** Utilisez les boutons rapides ci-dessous ou reformulez votre question !`;
  }
  
  // Message d'aide avec liens vers des sites
  private getHelpMessageWithSites(): string {
    const trustedSites = this.getTrustedSitesLinks();
    return `🤔 **Je n'ai pas pu trouver une réponse précise à votre question**

${trustedSites}

💡 **Conseil :** Essayez de reformuler votre question ou utilisez les boutons rapides ci-dessous pour des réponses instantanées !`;
  }

  // Obtenir le message de bienvenue
  getWelcomeMessage(): string {
    return `Bienvenue dans HanafiyaIA, votre assistant intelligent pour tout ce qui concerne l'Islam et la confrérie Tidiane.`;
  }

  // Obtenir les suggestions rapides (uniquement base de connaissances - maximum 3)
  async getQuickSuggestions(): Promise<Array<{ label: string; action: string }>> {
    const suggestions: Array<{ label: string; action: string }> = [];
    
    // Charger des suggestions depuis la base de connaissances
    try {
      const entries = await knowledgeBaseApiService.getAllEntries();
      
      if (entries && entries.length > 0) {
        // Le backend filtre déjà les entrées inactives (is_active=True)
        // Sélectionner un mélange équilibré de différentes catégories
        const categories = ['Pratique', 'Croyance', 'Histoire', 'Jurisprudence', 'Éthique'];
        const selectedEntries: any[] = [];
        
        // Prendre 1 question de chaque catégorie (priorité aux catégories importantes)
        for (const category of categories) {
          const categoryEntries = entries
            .filter((e: any) => e.category === category)
            .slice(0, 1); // Maximum 1 par catégorie
          selectedEntries.push(...categoryEntries);
        }
        
        // Si on n'a pas assez, prendre les plus récentes ou aléatoires
        if (selectedEntries.length < 3 && entries.length > selectedEntries.length) {
          const remaining = entries
            .filter((e: any) => !selectedEntries.some((se: any) => se.question === e.question))
            .slice(0, 3 - selectedEntries.length);
          selectedEntries.push(...remaining);
        }
        
        // Convertir en suggestions avec emojis - garder les questions complètes
        for (const entry of selectedEntries.slice(0, 3)) {
          // Garder la question complète sans troncature
          const question = entry.question.trim();
          
          // Ajouter un emoji selon la catégorie
          const emoji = this.getCategoryEmoji(entry.category);
          suggestions.push({
            label: `${emoji} ${question}`,
            action: question, // Utiliser la question exacte de la base
          });
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des suggestions:', error);
    }
    
    // Retourner seulement 3 suggestions maximum
    return suggestions.slice(0, 3);
  }
  
  // Obtenir un emoji selon la catégorie
  private getCategoryEmoji(category: string): string {
    const emojiMap: Record<string, string> = {
      'Pratique': '📿',
      'Croyance': '💡',
      'Histoire': '🎓',
      'Jurisprudence': '📚',
      'Éthique': '🤲',
      'Autre': '💬',
    };
    return emojiMap[category] || '💬';
  }
  
  // Obtenir les liens vers des sites islamiques fiables
  private getTrustedSitesLinks(): string {
    return `📚 **Pour approfondir, consultez ces sites islamiques fiables :**\n\n` +
      `• **SunnaSearch** (https://www.sunnasearch.fr/) - Moteur de recherche islamique francophone\n` +
      `• **IslamHouse** (https://islamhouse.com/fr/) - Ressources islamiques traduites\n` +
      `• **Coran en ligne** - Pour lire et étudier le Coran\n` +
      `• **Collections de Hadiths** - Sahih Bukhari, Sahih Muslim, etc.\n\n` +
      `💡 **Conseil :** Pour des questions complexes, consultez un imam ou un savant reconnu.`;
  }
}

export const chatService = new IslamicChatService();

